import React, { useState, useRef, useEffect } from 'react';
import { ArrowUp } from 'lucide-react';
import { useChat } from '../../hooks/useChat';

export const LONGUEUR_MAX_QUESTION = 500;
const SEUIL_COMPTEUR = 400;

// Question remise dans le champ par « Reformuler » : `id` change à chaque demande, pour
// qu'une même question puisse être reprise deux fois.
export interface Brouillon {
  texte: string;
  id: number;
}

interface ChatInputProps {
  onSendMessage: (content: string) => void;
  isLoading: boolean;
  isPaidUser: boolean;
  isWelcomeMode?: boolean;
  brouillon?: Brouillon | null;
}

export const ChatInput: React.FC<ChatInputProps> = ({
  onSendMessage,
  isLoading,
  isWelcomeMode = false,
  brouillon = null,
}) => {
  const [text, setText] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { quotaInfo } = useChat();

  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 150)}px`;
    }
  }, [text]);

  useEffect(() => {
    if (!brouillon) return;
    setText(brouillon.texte.slice(0, LONGUEUR_MAX_QUESTION));
    textareaRef.current?.focus();
  }, [brouillon]);

  const canSendMessage = !isLoading; // Toujours ouvert : le backend sert la réponse de quota dépassé

  const handleSend = () => {
    if (text.trim() && canSendMessage) {
      onSendMessage(text.trim());
      setText('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const getPlaceholder = () => {
    if (isLoading) return 'Nia consulte les données…';
    return 'Posez votre question sur la BRVM…';
  };

  return (
    <div className={`chat-input-container ${isWelcomeMode ? 'mode-welcome' : ''}`}>
      <div className={`input-wrapper ${isWelcomeMode ? 'input-wrapper--large' : ''}`}>
        <textarea
          ref={textareaRef}
          className="chat-input-field"
          placeholder={getPlaceholder()}
          aria-label="Votre question à Nia"
          value={text}
          maxLength={LONGUEUR_MAX_QUESTION}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={!canSendMessage}
          rows={isWelcomeMode ? 3 : 1}
        />
        <button
          type="button"
          className="btn-send"
          onClick={handleSend}
          disabled={!text.trim() || !canSendMessage}
          aria-label="Envoyer le message"
        >
          <ArrowUp size={18} />
        </button>
      </div>

      <div className="chat-input-footer">
        <span>
          {quotaInfo && !quotaInfo.is_pro && (
            <>
              <strong className={quotaInfo.remaining > 0 ? 'quota-ok' : 'quota-epuise'}>
                {quotaInfo.remaining} / {quotaInfo.limit}
              </strong>{' '}
              messages restants aujourd'hui
            </>
          )}
          {quotaInfo?.is_pro && 'Nia décrit les données, elle ne donne pas de conseil en investissement.'}
        </span>
        {text.length >= SEUIL_COMPTEUR && (
          <span className={text.length >= LONGUEUR_MAX_QUESTION ? 'quota-epuise' : undefined}>
            {text.length} / {LONGUEUR_MAX_QUESTION}
          </span>
        )}
      </div>
    </div>
  );
};
